import { useEffect, useMemo, useState } from 'react'
import { EmptyIcon } from '@assets/svgs/EmptyIcon'
import { SimulationCard } from './components/SimulationCard'
import { SimulationCardSkeleton } from './components/SimulationCardSkeleton'
import { SimulationsPagination } from './components/SimulationsPagination'
import { SimulationsHeader } from './components/SimulationsHeader'

import type { SimulationsListProps } from './types'

import * as S from './styles'

const ITEMS_PER_PAGE = 6

export const SimulationsList = ({ simulations, isLoading, onEdit, onDelete, onNewSimulation, canEdit = true, canEditSimulations = true }: SimulationsListProps) => {
  const [currentPage, setCurrentPage] = useState(1)

  // Ordenar simulações da mais recente para a mais antiga
  const sortedSimulations = useMemo(() => {
    if (!simulations || simulations.length === 0) return []
    return [...simulations].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateB - dateA
    })
  }, [simulations])

  // Paginação
  const totalPages = Math.ceil(sortedSimulations.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedSimulations = sortedSimulations.slice(startIndex, endIndex)

  // Resetar página quando a lista mudar
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  return (
    <S.Container>
      <SimulationsHeader
        onNewSimulation={canEdit ? onNewSimulation : undefined}
        canEditSimulations={canEditSimulations}
      />
      
      {/* Estado de loading */}
      {isLoading ? (
        <S.Grid>
          {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
            <SimulationCardSkeleton key={index} />
          ))}
        </S.Grid>
      ) : !simulations || simulations.length === 0 ? (
        // Estado vazio
        <S.EmptyState>
          <S.EmptyIcon>
            <EmptyIcon />
          </S.EmptyIcon>
          <S.EmptyMessage>Nenhum item encontrado</S.EmptyMessage>
        </S.EmptyState>
      ) : (
        // Lista com dados
        <>
          <S.Grid>
            {paginatedSimulations.map((simulation) => (
              <SimulationCard
                key={simulation.id}
                simulation={simulation}
                onEdit={onEdit}
                onDelete={onDelete}
                canEdit={canEdit}
              />
            ))}
          </S.Grid>
          {totalPages >= 1 && (
            <SimulationsPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          )}
        </>
      )}
    </S.Container>
  )
}
